import {
  Autocomplete,
  Drawer as MatDrawer,
  FormControl,
  IconButton,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import { filterDrawer, selectOpen, } from '../../../../features/layout/layoutSlice';
import { useAppDispatch } from '../../../store';
import { AppLink, AppLinkCategory } from '../../../utils/routes';
import { InlineGridTemplate } from '../../controls/Grid/InlineGridTemplate';
import useDebounce from '../../hooks/useDebounce';
import { CollapsedMenu } from './CollapsedDrawerMenu';
import { DrawerMenuProps, DrawerMenuRef } from './drawerMenu';
import { useStyles } from './drawerStyles';
import { DrawerToolbar } from './DrawerToolbar';
import { ExpandedMenu } from './ExpandedDrawerMenu';
import { VersionToolbar } from './VersionToolbar';
import { useTranslation } from 'react-i18next';
import { systemparameterQueryApiService } from '../../../../api/rtk-queries/extensions/systemparameterQueryApiService';
import { isJaVedriFilter } from '../../../utils/filtersHelper';


export function Drawer(props: {
  routes: AppLinkCategory[];
  className: string;
}): JSX.Element {
  const classes = useStyles();

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));
  const open = useSelector(selectOpen) && matches;
  const [filterTerm, setFilterTerm] = useState('');
  const [appLinks, setAppLinks] = useState<AppLink[]>([]);
  const [gotoPage, setGotoPage] = useState<AppLink | undefined>(undefined);
  const ref = useRef<DrawerMenuRef>(null);

  const filter = useDebounce(filterTerm, 150);
  useEffect(() => {
    dispatch(filterDrawer(filter));
  }, [dispatch, filter]);

  const refSearch = useRef();
  const { data: modul_eiendom } = systemparameterQueryApiService.api
    .useSjekkSystemparameterQuery({ id: 'modul_eiendom', condition: isJaVedriFilter });

  const routesPredicate = useMemo(() => {
    if (!modul_eiendom) {
      return (r: AppLinkCategory) => r.id !== 'M_EI';
    }
    return (r: AppLinkCategory) => true;
  }, [modul_eiendom]);

  const routes = useMemo(() => props.routes.filter(r => routesPredicate(r)), [props.routes, routesPredicate]);


  useEffect(() => {
    let entries: AppLink[] = [];
    routes.forEach((category) => {
      category.entries.forEach((entry) => {
        entries = [...entries, entry];
      });
    });
    setAppLinks(entries);
  }, [routes]);



  const location = useLocation();
  const navigate = useNavigate();
  const refDrawer = useRef(null);
  const [drawerProps, setDrawerProps] = useState<DrawerMenuProps>(
    {
      categories: routes,
      classes: classes,
      selectedCategory: props.routes.find((category) =>
        category.entries.some((e) => location.pathname.includes(e.path))),
      path: location.pathname
    }
  );

  const findCategory = useCallback((link: AppLink) => {
    return props.routes.find((category) => category.entries.some((e) => location.pathname.includes(e.path)));
  }, [location.pathname, props.routes]);

  useEffect(() => {
    setDrawerProps({
      categories: routes,
      classes: classes,
      selectedCategory: props.routes.find((category) =>
        category.entries.some((e) => location.pathname.includes(e.path))),
      path: location.pathname
    });
  }, [routes, classes, location.pathname, props.routes]);



  function collapseAll() {
    if (ref?.current?.setExpanded) {
      ref.current.setExpanded([]);
    }
  }


  const [focusSearch, setFocusSearch] = useState(false);
  const [enterSearch, setEnterSearch] = useState(false);
  return (
    <MatDrawer
      PaperProps={{
        style: {
          position: 'initial',
          border: 'none',
        },
      }}
      ref={refDrawer}
      variant="permanent"
      className={`${props.className} ${classes.drawer} ${open ? 'open' : 'closed'
        }`}
    >
      <DrawerToolbar open={open} />
      <FormControl className={classes.mainNav}>
        <InlineGridTemplate
          gridTemplateColumns={open ? 'auto 2rem' : 'auto 0'}
          wrap={false}
          gap={0}
        >
          <Autocomplete<AppLink>
            renderInput={(params) => (
              <TextField {...params} label="Gå til" margin="normal" InputLabelProps={{ shrink: true }} />
            )}

            closeText='Lukk'
            openText='åpen'
            clearText='Klar'
            value={gotoPage ?? null}
            className={`${classes.searchinput}${!open && (focusSearch || enterSearch)
                ? ' ' + classes.overflowSearch
                : ''
              }`}
            options={[...appLinks]}
            onFocus={() => setFocusSearch(true)}
            onBlur={() => {
              setFocusSearch(false);
              setGotoPage(undefined);
            }}
            ref={refSearch}
            onMouseEnter={() => setEnterSearch(true)}
            onMouseLeave={() => {
              setEnterSearch(false);
              setGotoPage(undefined);
            }}
            getOptionLabel={(option) => option?.label ?? ''}
            isOptionEqualToValue={(option, value) =>
              value && option.id === value.id
            }
            onChange={(e, selected) => {
              if (selected) {
                navigate(selected?.path ?? '');
                const category = findCategory(selected);
                if (ref?.current?.setExpanded && category) {
                  ref?.current?.setExpanded((prev) => [...prev.filter(i => i.id !== category.id), category]);
                }
              }
              setFilterTerm(selected?.label ?? '');
              setGotoPage(selected as AppLink);
            }}
          />
          <Tooltip title={'Lukk alle'}>
            <IconButton
              onClick={collapseAll}
              hidden={!open}
              color="inherit"
              size="small"
              aria-label={t('Lukk alle')}
            >
              <ArrowCircleUpIcon />
            </IconButton>
          </Tooltip>

        </InlineGridTemplate>
        {open ? (
          <ExpandedMenu {...drawerProps} ref={ref} />
        ) : (
          <CollapsedMenu {...drawerProps} />
        )}
      </FormControl>
      <VersionToolbar className={classes.bottomRow} />
    </MatDrawer>
  );
}
